import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { DVF_PROPERTY_TYPES, type Commune, type DvfPropertyType, type DvfResponse } from '@app/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiErrorMessage } from '@/lib/api';
import { formatRatio } from '@/lib/format';
import { useAssumptions } from '@/features/simulations/queries';
import { CommuneSearch } from './CommuneSearch';
import { useCommuneDvf, useRefreshCommuneDvf } from './queries';

const eurosPerM2Formatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const formatEurosPerM2 = (value: number | null) => (value === null ? 'n.a' : eurosPerM2Formatter.format(value));

const formatDate = (iso: string) => new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(new Date(iso));

function CalibrationCard({ result }: { result: DvfResponse }) {
  const assumptions = useAssumptions();
  const { annualGrowth, endpointGrowth, yearsUsed, warnings } = result.calibration;
  const current = assumptions.data?.housingPriceGrowth ?? null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Calibrage de la croissance des prix</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <div className="text-xs text-muted-foreground">Mesurée (régression, {yearsUsed.length} ans)</div>
            <div className="text-lg font-semibold">{formatRatio(annualGrowth)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Mesurée (première → dernière année)</div>
            <div className="text-lg font-semibold">{formatRatio(endpointGrowth)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Hypothèse actuelle du MMI</div>
            <div className="text-lg font-semibold">{formatRatio(current)}</div>
          </div>
        </div>
        <ul className="list-disc pl-5 text-muted-foreground">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
        <p className="text-muted-foreground">
          Repère à comparer, jamais appliqué automatiquement : rien n’est enregistré sans votre validation.
        </p>
        {annualGrowth !== null && (
          <div>
            <Button asChild size="sm" variant="outline">
              <Link to="/simulateur/hypotheses" search={{ housingPriceGrowth: Math.round(annualGrowth * 10_000) / 10_000 }}>
                Reprendre {formatRatio(annualGrowth)} dans les hypothèses
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function YearsTable({ result }: { result: DvfResponse }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th className="p-2 text-left font-medium">Année</th>
            <th className="p-2 text-right font-medium">Ventes retenues</th>
            <th className="p-2 text-right font-medium">€/m² bas (25 %)</th>
            <th className="p-2 text-right font-medium">€/m² médian</th>
            <th className="p-2 text-right font-medium">€/m² haut (75 %)</th>
            <th className="p-2 text-right font-medium">Surface médiane</th>
          </tr>
        </thead>
        <tbody>
          {result.years.map((row) => (
            <tr key={row.year} className="border-b border-border/50">
              <td className="p-2">
                {row.year}
                {!row.reliable && <span className="ml-2 text-xs text-destructive">peu fiable</span>}
              </td>
              <td className="p-2 text-right tabular-nums">{row.salesCount}</td>
              <td className="p-2 text-right tabular-nums">{formatEurosPerM2(row.p25PricePerM2)}</td>
              <td className="p-2 text-right font-medium tabular-nums">{formatEurosPerM2(row.medianPricePerM2)}</td>
              <td className="p-2 text-right tabular-nums">{formatEurosPerM2(row.p75PricePerM2)}</td>
              <td className="p-2 text-right tabular-nums">{row.medianSurfaceM2 === null ? 'n.a' : `${row.medianSurfaceM2} m²`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommuneMarket({ commune }: { commune: Commune }) {
  const [propertyType, setPropertyType] = useState<DvfPropertyType>('Appartement');
  const dvf = useCommuneDvf(commune.code, propertyType);
  const refresh = useRefreshCommuneDvf(commune.code, propertyType);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">
          {commune.name} <span className="text-sm font-normal text-muted-foreground">INSEE {commune.code}</span>
        </h2>
        <div className="flex gap-2" role="group" aria-label="Type de bien">
          {DVF_PROPERTY_TYPES.map((type) => (
            <Button
              key={type}
              size="sm"
              variant={type === propertyType ? 'default' : 'outline'}
              aria-pressed={type === propertyType}
              onClick={() => setPropertyType(type)}
            >
              {type}
            </Button>
          ))}
        </div>
      </div>

      {dvf.isPending && <p className="text-muted-foreground">Lecture des ventes DVF… la première fois, quelques secondes.</p>}
      {dvf.isError && <p className="text-destructive">{apiErrorMessage(dvf.error, 'Données DVF indisponibles')}</p>}

      {dvf.data && (
        <>
          <CalibrationCard result={dvf.data} />
          <YearsTable result={dvf.data} />
          {dvf.data.missingYears.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Fichiers absents chez l’Etalab : {dvf.data.missingYears.join(', ')}.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span>
              Données du {formatDate(dvf.data.fetchedAt)}
              {dvf.data.fromCache ? ' (en cache)' : ''}
            </span>
            <Button size="sm" variant="outline" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
              {refresh.isPending ? 'Actualisation…' : 'Actualiser depuis DVF'}
            </Button>
            {refresh.isError && <span className="text-destructive">{apiErrorMessage(refresh.error, 'Actualisation impossible')}</span>}
          </div>
        </>
      )}
    </div>
  );
}

export function MarketExplorer() {
  const [commune, setCommune] = useState<Commune | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <Alert>
        <AlertDescription>
          Ventes notariées DVF (Etalab), un logement par vente. Le prix rapporté est celui de la vente : pour une
          maison il inclut le terrain, et la surface est la surface bâtie, pas la surface Carrez. L’Alsace-Moselle et
          Mayotte ne sont pas couvertes.
        </AlertDescription>
      </Alert>
      <CommuneSearch onSelect={setCommune} />
      {commune !== null && <CommuneMarket key={commune.code} commune={commune} />}
    </div>
  );
}
